import * as React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { Button, Text, Tooltip, makeStyles, tokens } from "@fluentui/react-components";
import { Delete16Regular, Attach24Regular } from "@fluentui/react-icons";
import { useTypographyStyles } from "../../../styles/typography.styles";
import { validateAttachmentFile, ALLOWED_CONTENT_TYPES } from "../attachmentConstraints";

/**
 * Attachment staging for the create-discussion wizard.
 *
 * The upload API is keyed by discussion ID, so nothing can actually be
 * uploaded until the discussion exists. This component therefore holds the
 * chosen Files in memory and renders local previews; CreateDiscussionDialog
 * uploads them once the create call returns an ID.
 *
 * Deliberately NOT a reuse of DiscussionAttachments — that one owns a
 * server-backed list (fetch / upload / delete / lightbox) for an existing
 * discussion, and every one of those paths needs an ID. The two share their
 * limits via attachmentConstraints so validation can't drift.
 */

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalS,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
  },
  dropZone: {
    display: "flex",
    flexWrap: "wrap",
    gap: tokens.spacingHorizontalM,
    minHeight: "72px",
    padding: tokens.spacingVerticalS,
    borderRadius: tokens.borderRadiusMedium,
    border: `1px dashed ${tokens.colorNeutralStroke2}`,
    alignItems: "center",
  },
  dropZoneActive: {
    // Re-declare the whole `border` shorthand — Griffel disallows
    // shorthand-in-one-rule + longhand-in-another because it can't guarantee
    // the merge order at runtime.
    border: `1px dashed ${tokens.colorBrandStroke1}`,
    backgroundColor: tokens.colorBrandBackground2,
  },
  thumb: {
    position: "relative",
    width: "72px",
    height: "72px",
    borderRadius: tokens.borderRadiusMedium,
    overflow: "hidden",
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  thumbImage: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
  },
  removeButton: {
    position: "absolute",
    top: "2px",
    right: "2px",
    minWidth: "24px",
    width: "24px",
    height: "24px",
    padding: 0,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    padding: tokens.spacingVerticalS,
    backgroundColor: tokens.colorPaletteRedBackground1,
    borderRadius: tokens.borderRadiusMedium,
  },
});

interface Props {
  files: File[];
  onFilesChange: (files: File[]) => void;
  /** Set while the create request is in flight so files can't be edited mid-submit. */
  disabled?: boolean;
}

const StagedAttachments: React.FC<Props> = ({ files, onFilesChange, disabled }) => {
  const styles = useStyles();
  const typography = useTypographyStyles();
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const [previews, setPreviews] = useState<string[]>([]);

  // One object URL per staged file, rebuilt whenever the list changes. The
  // cleanup revokes the previous batch, so nothing leaks across edits or on
  // unmount (closing the dialog).
  useEffect(() => {
    const urls = files.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [files]);

  const addFiles = useCallback(
    (incoming: File[]) => {
      if (disabled) return;
      setError(null);
      const accepted: File[] = [];
      for (const file of incoming) {
        const reason = validateAttachmentFile(file);
        if (reason) {
          setError(reason);
          continue;
        }
        accepted.push(file);
      }
      if (accepted.length > 0) onFilesChange([...files, ...accepted]);
    },
    [files, onFilesChange, disabled],
  );

  // Paste-to-attach, mirroring DiscussionThread's handler so the wizard
  // behaves the same way. Registered in the CAPTURE phase deliberately:
  // DiscussionThread keeps its own bubble-phase listener on `document` and
  // can be mounted behind this dialog (Discussions.tsx renders the thread as
  // main content while the create dialog sits above it). Capture runs first,
  // so stopPropagation here keeps a pasted screenshot from ALSO being
  // uploaded to whatever thread happens to be open underneath.
  useEffect(() => {
    if (disabled) return;
    const handlePaste = (e: ClipboardEvent) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      const pasted: File[] = [];
      for (const item of Array.from(items)) {
        if (item.kind === "file") {
          const f = item.getAsFile();
          if (f) pasted.push(f);
        }
      }
      // Only intercept when the clipboard actually carries files, so ordinary
      // text paste into the title/description still works as before.
      if (pasted.length > 0) {
        e.preventDefault();
        e.stopPropagation();
        addFiles(pasted);
      }
    };
    document.addEventListener("paste", handlePaste, true);
    return () => document.removeEventListener("paste", handlePaste, true);
  }, [addFiles, disabled]);

  const removeAt = (index: number) => {
    if (disabled) return;
    onFilesChange(files.filter((_, i) => i !== index));
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Text className={typography.formLabel}>Attachments</Text>
        <Tooltip content="Attach an image" relationship="label">
          <Button
            appearance="subtle"
            icon={<Attach24Regular />}
            disabled={disabled}
            onClick={() => inputRef.current?.click()}
          />
        </Tooltip>
      </div>

      <input
        ref={inputRef}
        type="file"
        multiple
        accept={ALLOWED_CONTENT_TYPES.join(",")}
        style={{ display: "none" }}
        onChange={(e) => {
          addFiles(Array.from(e.target.files ?? []));
          // Reset value so choosing the same file twice re-fires onChange.
          if (inputRef.current) inputRef.current.value = "";
        }}
      />

      <div
        className={`${styles.dropZone} ${isDragOver ? styles.dropZoneActive : ""}`}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setIsDragOver(true);
        }}
        onDragLeave={() => setIsDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setIsDragOver(false);
          addFiles(Array.from(e.dataTransfer.files ?? []));
        }}
      >
        {files.length === 0 ? (
          <Text className={typography.caption}>
            Paste a screenshot, drop an image here, or use the paperclip.
          </Text>
        ) : (
          files.map((file, i) => (
            <div key={`${file.name}-${file.lastModified}-${i}`} className={styles.thumb}>
              {previews[i] && <img src={previews[i]} alt={file.name} className={styles.thumbImage} />}
              <Tooltip content="Remove" relationship="label">
                <Button
                  className={styles.removeButton}
                  appearance="subtle"
                  icon={<Delete16Regular />}
                  disabled={disabled}
                  onClick={() => removeAt(i)}
                />
              </Tooltip>
            </div>
          ))
        )}
      </div>

      {error && <Text className={`${typography.bodySmall} ${styles.errorMessage}`}>{error}</Text>}
    </div>
  );
};

export default StagedAttachments;
